import Modal from '@ui/Modal/component'
import SearchMap, { MAP_MODE } from '@ui/Search/Map/component'
import ElementPropertiesManager from 'assets/core/js/module/elementPropertiesManager'
import type { ModalType } from '@ui/Modal/component'
import type { QueryResponse } from '@ui/Search/Map/component'

export interface MapConfig {
  callbacks?: {
    [key in (typeof callbacksAvailable)[number]]?: ((this: void, map: SearchMapModal, ...args: unknown[]) => void)[]
  }
}

const callbacksAvailable = ['onInit', 'onLoad', 'onQuery', 'onMove', 'onMarkerClick', 'onModalOpen', 'onModalClose'] as const

export default class SearchMapModal {
  element!: HTMLElement
  config!: MapConfig
  map!: SearchMap | null
  modal!: ModalType | null
  isInit!: boolean
  isMapAvailable!: boolean

  constructor(element: string | HTMLElement, userConfig?: MapConfig) {
    this.element = this.resolveElement(element)

    if (!this.element?.hasAttribute('data-map-boundaries')) {
      return
    }

    this.config = userConfig ?? {}
    this.isInit = false
    this.isMapAvailable = false

    this.initConfig()

    ElementPropertiesManager.addProperty<SearchMapModal>(this.element, 'searchMapModal', this)
  }

  private initConfig(): void {
    this.config.callbacks = {
      ...Object.fromEntries(callbacksAvailable.map((cb) => [cb, []])),
      ...this.config.callbacks,
    } as MapConfig['callbacks']
  }

  private fireCallbacks(name: (typeof callbacksAvailable)[number], ...args: unknown[]): void {
    this.config.callbacks?.[name]?.forEach((cbFn) => {
      cbFn(this, ...args)
    })
  }

  private resolveElement(element: string | HTMLElement): HTMLElement {
    const el = typeof element === 'string' ? document.querySelector<HTMLElement>(element) : element

    if (!el) {
      throw new Error('Missing element.')
    }

    return el
  }

  open(): void {
    if (!this.modal) {
      this.initModal()
    }

    this.modal?.open()
  }

  initModal(): ModalType | undefined {
    const modalMapEl = document.getElementById('search-map-modal')

    if (!modalMapEl) {
      return
    }

    this.modal = Modal(modalMapEl, {
      onOpen: () => {
        if (!this.isMapAvailable) {
          this.initMap()
        }

        this.fireCallbacks('onModalOpen')
      },
      onBeforeOpen: () => {
        this.map?.getSliderElement()?.removeAttribute('hidden')
      },
      onBeforeClose: () => {
        this.map?.getSliderElement()?.setAttribute('hidden', 'true')
      },
      onClose: () => {
        this.fireCallbacks('onModalClose')
      },
    })

    return this.modal
  }

  initMap(): void {
    const modalTitleEl = this.modal?.getElement().querySelector<HTMLElement>('.o-modal__heading-title')

    this.fireCallbacks('onInit')

    this.map = new SearchMap(
      this.element,
      {
        mode: MAP_MODE.QUERY,
        onLoad: () => {
          this.isMapAvailable = true

          this.fireCallbacks('onLoad')
        },
        onQuery: (response: QueryResponse) => {
          this.fireCallbacks('onQuery', response)
        },
        onMove: () => {
          if (this.modal?.getElement().hasAttribute('data-move-title') && modalTitleEl) {
            modalTitleEl.innerHTML = this.modal.getElement().getAttribute('data-move-title') as string

            this.modal.getElement().removeAttribute('data-move-title')
          }

          this.fireCallbacks('onMove')
        },
        onMarkerClick: (markerId) => {
          this.fireCallbacks('onMarkerClick', markerId)
        },
      },
      []
    )
  }
}
